import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

const LINKS = [
  { label: "Manifesto", id: "manifesto", type: "scroll" },
  { label: "About", id: "about", type: "route", to: "/about" },
  { label: "Workshops", id: "workshops", type: "scroll" },
  { label: "Voices", id: "testimonials", type: "scroll" },
  { label: "Contact", id: "contact", type: "scroll" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToId = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const go = (link) => {
    setOpen(false);
    if (link.type === "route") {
      navigate(link.to);
      return;
    }
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(link.id), 200);
    } else {
      scrollToId(link.id);
    }
  };

  const goHome = () => {
    setOpen(false);
    if (location.pathname !== "/") navigate("/");
    else scrollToId("hero");
  };

  const goContact = () => go({ type: "scroll", id: "contact" });

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-ink border-b border-line-d" : "bg-transparent"
      }`}
      data-testid="site-nav"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-24 md:h-28 flex items-center justify-between gap-6">
        <button onClick={goHome} className="text-left shrink-0" data-testid="nav-logo">
          <span className="block font-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-sand tracking-tight leading-[1.1] whitespace-nowrap">Beyond the Mask</span>
          <span className="block font-mono text-[9px] md:text-[10px] tracking-[0.28em] uppercase text-muted-d mt-2 whitespace-nowrap">Trust · Vulnerability · Connection</span>
        </button>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8 shrink-0">
          {LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l)}
              className="font-mono text-[11px] tracking-[0.15em] uppercase text-muted-d hover:text-sand transition-colors duration-300 whitespace-nowrap"
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={goContact}
            className="font-mono text-[11px] tracking-[0.15em] uppercase text-ink bg-sand px-5 py-2.5 hover:bg-terra transition-colors duration-300 whitespace-nowrap"
            data-testid="nav-cta"
          >
            Book a call
          </button>
        </nav>

        <button className="lg:hidden text-sand shrink-0" onClick={() => setOpen(!open)} data-testid="nav-menu-toggle">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-ink border-b border-line-d"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button key={l.id} onClick={() => go(l)} className="text-left font-display text-2xl text-sand" data-testid={`nav-mobile-${l.id}`}>
                  {l.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
